import { PRICING_TABLES } from "../../../utils/pricing/constants"
import { InvoiceDto } from "../../invoice/models"
import { ORDERS } from "../../order/Constants"
import { OrderDto } from "../../order/Order"
import { QuoteDto } from "../../quote/Quote"
import { RowDto } from "../../rows/Row"
import { TaxDto } from "../../tax/Tax"
import Group, { GroupDto } from "../Group"
import { PricingFeeCalculationDto } from "../PricingFeeCalculation"
import PricingRow, { PricingRowDto } from "../PricingRow"
import { PricingTotalDto } from "../PricingTotals"
import { PricingTable } from "../models"
import { PatchPayload } from "../services"
import { PricingTableItemsStore } from "./types"

export const Handler = (state: PricingTableItemsStore) => {
    return {
        getInitialState(): PricingTableItemsStore {
            return {
                pricingTableItems: new Map<string, PricingRowDto>(),
                pricingTableGroups: new Map<string, GroupDto>(),
                pricingTableGroupItems: new Map<string, string[]>(),
                pricingTableId: new Map<string, string>(),
                pricingTableSelectedItems: new Map<string, Set<string>>(),
                pricingTableTax: new Map<string, Map<string, TaxDto>>(),
                pricingTableFees: new Map<string, PricingFeeCalculationDto[]>(),
                pricingTableTotals: new Map<string, PricingTotalDto[]>(),
                isBottomPriceEnabled: false,
                isWorkflowLoading: false,
            }
        },

        setPricingTableId(payload: { pricingTableId: string, objectId: string }): PricingTableItemsStore {
            const objectId = payload.objectId
            const pricingTableId = payload.pricingTableId
            state.pricingTableId.set(objectId, pricingTableId)
            return {...state}
        },

        setPricingTable(payload: InvoiceDto | QuoteDto | OrderDto | PricingTable) {
            state = this.getInitialState()
            const items = payload.attributes.items as PricingRowDto[]
            state = this.setPricingTableItems({
                pricingTableObjectId: payload.id,
                pricingTableItems: items
            })

            state = this.setPricingTableGroups({
                pricingTableObjectId: payload.id,
                pricingTableGroups: payload.attributes.groups
            })

            payload.attributes.groups.forEach((group: GroupDto) => {
                const items = payload.attributes.items as PricingRowDto[]
                state = this.setPricingTableGroupItems({
                    objectId: payload.id,
                    groupId: group.id,
                    groupItems: items
                        .filter((item: PricingRowDto) => item.attributes.groupId == group.id)
                        .map((item: PricingRowDto) => item.id)
                })
            })

            state = this.setPricingTableTotals({
                objectId: payload.id,
                totals: payload.attributes.totals
            })

            const taxesMap = new Map<string, TaxDto>()
            payload.attributes.totals.forEach(total => {
                if (total.attributes.billingMethod && total.attributes.tax) {
                    taxesMap.set(total.attributes.billingMethod, total.attributes.tax)
                }
            })
            state = this.setPricingTableTaxes({
                objectId: payload.id,
                taxes: taxesMap
            })

            let fees: PricingFeeCalculationDto[] = []
            if (( payload.attributes.totals.length > 0 && payload.attributes.totals[0].attributes.feeCalculations.length > 0)) {
                fees = payload.attributes.totals[0].attributes.feeCalculations
            }

            state = this.setPricingTableFees({
                objectId: payload.id,
                fees: fees
            })


            let tableId = payload.id
            if (payload.type !== PRICING_TABLES) {
                tableId = payload.attributes.pricingTableId
            }

            if(payload.type === ORDERS) {
                state.isWorkflowLoading = payload.attributes.isWorkflowLoading
            }

            state = this.setPricingTableId({
                pricingTableId: tableId,
                objectId: payload.id
            })
            return {...state}
        },

        setPricingTableGroupItems(payload: { objectId: string, groupId: string, groupItems: string[] }) {
            const objectId = payload.objectId as string
            const groupId = payload.groupId as string
            const itemIds = payload.groupItems as string[]

            state.pricingTableGroupItems.set(`${objectId}_${groupId}`, itemIds)
            return {...state}
        },

        setPricingTableTotals(payload: { objectId: string, totals: PricingTotalDto[] }): PricingTableItemsStore {
            const objectId = payload.objectId as string
            const totals = payload.totals as PricingTotalDto[]
            state.pricingTableTotals.set(objectId, totals)
            return {...state}
        },

        setPricingTableTaxes(payload: { objectId: string, taxes: Map<string, TaxDto> }): PricingTableItemsStore {
            const objectId = payload.objectId as string
            const taxes = payload.taxes as Map<string, TaxDto>
            state.pricingTableTax.set(objectId, taxes)
            return {...state}
        },

        setPricingTableFees(payload: { objectId: string, fees: PricingFeeCalculationDto[] | undefined }): PricingTableItemsStore {
            const objectId = payload.objectId as string
            if (payload.fees) {
                const fees = payload.fees as PricingFeeCalculationDto[]
                state.pricingTableFees.set(objectId, fees)
            }
            return {...state}
        },

        addItemToSelectedItems(payload: { objectId: string, item: string }): PricingTableItemsStore {
            const objectId = payload.objectId as string
            const selectedItemsItemAdd = payload.item as string
            let setOfItems = state.pricingTableSelectedItems.get(objectId)
            if (!setOfItems) {
                setOfItems = new Set<string>()
            }
            setOfItems.add(selectedItemsItemAdd)
            state.pricingTableSelectedItems.set(objectId, new Set(setOfItems))
            return {...state}
        },

        removeItemFromSelectedItems(payload: { objectId: string, item: string }): PricingTableItemsStore {
            const objectId = payload.objectId as string
            const selectedItemsItemRemove = payload.item as string
            let setOfItemsRemove = state.pricingTableSelectedItems.get(objectId)
            if (!setOfItemsRemove) {
                setOfItemsRemove = new Set<string>()

            } else {
                setOfItemsRemove.delete(selectedItemsItemRemove)
            }
            state.pricingTableSelectedItems.set(objectId, new Set(setOfItemsRemove))
            return {...state}
        },

        setPricingTableItems(payload: { pricingTableObjectId: string, pricingTableItems: PricingRowDto[] | RowDto[] }
        ): PricingTableItemsStore {
            const objectId = payload.pricingTableObjectId as string
            const pricingTableItems = payload.pricingTableItems as PricingRowDto[]
            if (pricingTableItems && pricingTableItems.length > 0) {
                pricingTableItems.map(item => {
                    state.pricingTableItems.set(`${objectId}_${item.id}`, item)
                })
            }
            return {...state}
        },

        setPricingTableGroups(payload: { pricingTableObjectId: string, pricingTableGroups: GroupDto[] }): PricingTableItemsStore {
            const objectId = payload.pricingTableObjectId as string
            const pricingTableGroups = payload.pricingTableGroups as GroupDto[]
            pricingTableGroups.map(item => {
                state.pricingTableGroups.set(`${objectId}_${item.id}`, item)
            })
            return {...state}
        },

        setPricingTableItem(payload: { itemId: string, objectId: string, item: PricingRowDto }): PricingTableItemsStore {
            const itemId = payload.itemId as string
            const objectId = payload.objectId as string
            const item = payload.item as PricingRowDto
            state.pricingTableItems.set(`${objectId}_${itemId}`, item)
            return {...state}
        },

        bulkPatchPricingTable(
            payload: {
                documentId: string,
                payload: PatchPayload,
            }
        ) {
            if (payload.payload.rows) {
                for (const [id,] of Object.entries(payload.payload.rows)) {
                    const item = state.pricingTableItems.get(`${payload.documentId}_${id}`)
                    if (item) {
                        for (const [key, value] of Object.entries(payload.payload.rows[id] ?? {})) {
                            item.attributes[key] = value
                        }
                        state.pricingTableItems.set(`${payload.documentId}_${id}`, item)
                    }
                }
            }
            return {...state}
        },

        setPricingTableGroup(payload: { groupId: string, objectId: string, group: GroupDto }): PricingTableItemsStore {
            const groupId = payload.groupId as string
            const objectId = payload.objectId as string
            const group = payload.group as GroupDto
            state.pricingTableGroups.set(`${objectId}_${groupId}`, group)
            return {...state}
        },

        setPricingTableSelectedItems(payload: { objectId: string, items: Set<string> }): PricingTableItemsStore {
            const objectId = payload.objectId as string
            const selectedItemsSet = payload.items as Set<string>
            state.pricingTableSelectedItems.set(`${objectId}`, new Set(selectedItemsSet))
            return {...state}
        },

        setBottomPriceEnabled(payload: { isBottomPriceEnabled: boolean }): PricingTableItemsStore {
            state.isBottomPriceEnabled = payload.isBottomPriceEnabled as boolean
            return {...state}
        },

        setIsWorkflowLoading(payload: boolean): PricingTableItemsStore {
            state.isWorkflowLoading = payload as boolean
            return {...state}
        },

        patchPricingTableItem(
            payload: {
                item : PricingRow,
                id : string,
                objectId : string,
                updateGroup: boolean,
                updateTotals : boolean
            }
        ) : PricingTableItemsStore {
            const groupId = payload.item.getDto().attributes.groupId
            if (payload.updateGroup && groupId) {
                const group = new Group(payload.item.getMetadata().group)
                if (group) {
                    state.pricingTableGroups.set(`${payload.objectId}_${groupId}`, group.getDto())
                }
            }

            const totals = payload.item.getMetadata().totals
            if (payload.updateTotals && totals) {  
                state.pricingTableTotals.set(payload.objectId, totals)
            }

            const additionalRows = payload.item.getMetadata().additionalRows
            if (additionalRows && additionalRows.length > 0) {
                additionalRows.map(item => {
                    state.pricingTableItems.set(`${payload.objectId}_${item.id}`, item)
                })
            }

            state.pricingTableItems.set(`${payload.objectId}_${payload.id}`, payload.item.getDto())
            return {...state}
        },

        deletePricingTableItem(payload: {
            itemId : string,
            item: PricingRow,
            objectId : string
        }) : PricingTableItemsStore {
            const groupId = payload.item.getDto().attributes.groupId
            if (groupId) {
                const group = new Group(payload.item.getMetadata().group)
                if (group) {
                    state.pricingTableGroups.set(`${payload.objectId}_${groupId}`, group.getDto())
                }
            }

            const totals = payload.item.getMetadata().totals
            if (totals) {
                state.pricingTableTotals.set(payload.objectId, totals)
            }

            const additionalRows = payload.item.getMetadata().additionalRows
            if (additionalRows && additionalRows.length > 0) {
                additionalRows.map(item => {
                    state.pricingTableItems.set(`${payload.objectId}_${item.id}`, item)
                })
            }
            state.pricingTableItems.delete(`${payload.objectId}_${payload.itemId}`)
            return {...state}
        },

        patchPricingTable(
            payload: {
                documentId: string,
                payload: PatchPayload,
            }
        ) {
            if (payload.payload.rows) {
                for (const [id,] of Object.entries(payload.payload.rows)) {
                    const item = state.pricingTableItems.get(`${payload.documentId}_${id}`)
                    if (item) {
                        for (const [key, value] of Object.entries(payload.payload.rows[id] ?? {})) {
                            item.attributes[key] = value
                        }
                        state.pricingTableItems.set(`${payload.documentId}_${id}`, item)
                    }
                }
            }

            payload.payload.rowsOrder?.map(rowOrder => {
                rowOrder.map(itemId => {
                    const key = `${payload.documentId}_${itemId}`
                    const value = state.pricingTableItems.get(key) as PricingRowDto
                    state.pricingTableItems.delete(key)
                    state.pricingTableItems.set(key, value)
                })
            })

            payload.payload.groupsOrder?.map(groupId => {
                const key = `${payload.documentId}_${groupId}`
                const value = state.pricingTableGroups.get(key) as GroupDto
                state.pricingTableGroups.delete(key)
                state.pricingTableGroups.set(key, value)
            })

            return {...state}
        },

        patchPricingTableSuccess(res: InvoiceDto | QuoteDto | OrderDto | PricingTable) {
            return {...this.setPricingTable(res)}
        },

        patchPricingTablePending() {
            return {...state}
        }
    }
}


// WEBPACK FOOTER //
// ./src/state/pricing/reducers/handler.ts